import { Card, CardBody, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row, Spinner } from "reactstrap";
import {
  IoReceiptOutline,
  IoWalletOutline,
  IoAlertCircleOutline,
  IoCalendarOutline,
} from "react-icons/io5";
import {
  BarChart, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
  Line,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { endOfMonth, endOfYear, format, startOfMonth, startOfYear, subMonths, subYears } from "date-fns";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { printInvoiceStatus } from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import "./styles.css";
import StatCard from "../../../../components/Cards/StatCard";
import i18n from "../../../../i18n";
import useCurrency from "../../../../utils/helpers/currency";

const DATE_RANGE_OPTIONS = ["this_month", "last_month", "this_year", "last_year", "custom"];

function getDateRange(option, customStart, customEnd) {
  const now = new Date();
  switch (option) {
    case "last_month": {
      const d = subMonths(now, 1);
      return [startOfMonth(d), endOfMonth(d)];
    }
    case "this_year":
      return [startOfYear(now), endOfYear(now)];
    case "last_year": {
      const d = subYears(now, 1);
      return [startOfYear(d), endOfYear(d)];
    }
    case "custom":
      return [customStart, customEnd];
    case "this_month":
    default:
      return [startOfMonth(now), endOfMonth(now)];
  }
}


const PIPELINE_COLORS = {
  Draft: "#adb5bd",
  Sent: "#6C63FF",
  Accepted: "#20c997",
  Rejected: "#fa5252",
};

const tooltipStyle = { fontSize: "0.78rem", borderRadius: 8, border: "1px solid #eee", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" };

function RevenueBarChart({ data }) {
  return (
    <div style={{ flex: 1, minHeight: 250 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis 
            dataKey="month" 
            tick={{ fontSize: 11, fill: "#555" }} 
            axisLine={false} 
            tickLine={false}
            reversed={i18n.language === "ar"}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#555" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            width={50}
          />
          <Tooltip
            formatter={(value) => [`$${value.toLocaleString()}`, "Revenue"]}
            contentStyle={tooltipStyle}
            cursor={{ fill: "#f5f3ff" }}
          />
          <Line dataKey="value" stroke="#6C63FF" strokeWidth={2} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function QuotePipelineChart({ data }) {
  return (
    <PieChart width={120} height={120}>
      <Pie
        data={data}
        dataKey="count"
        nameKey="stage"
        cx={60}
        cy={60}
        innerRadius={30}
        outerRadius={52}
        paddingAngle={2}
      >
        {data.map((entry, i) => (
          <Cell key={i} fill={PIPELINE_COLORS[entry.stage]} />
        ))}
      </Pie>
      <Tooltip
        formatter={(value, name) => [value, name]}
        contentStyle={tooltipStyle}
      />
    </PieChart>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const { formatDate, datePickerFormat } = useSettings();
  const currency = useCurrency();
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [topCustomersData, setTopCustomersData] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [quotePipeline, setQuotePipeline] = useState([]);
  const [dateRangeOption, setDateRangeOption] = useState("this_month");
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedRange, setAppliedRange] = useState(() => getDateRange("this_month"));
  const [kpiData, setKpiData] = useState({
    revenueThisMonth: 0,
    invoicesIssued: 0,
    overdueBalance: 0
  });

  const getHomeData = async (range) => {
    setLoading(true);

    const [from, to] = range;
    const params = new URLSearchParams();
    if (from) params.append("date_from", format(from, "yyyy-MM-dd"));
    if (to) params.append("date_to", format(to, "yyyy-MM-dd"));

    const data = await get(`/sales/home?${params.toString()}`);
    if (data.success) {
      setTopCustomersData(data.top_customers);
      setMonthlyRevenue(data.monthly_revenue);
      setQuotePipeline(data.quote_pipeline);
      setKpiData({
        revenueThisMonth: data.revenue_this_month,
        invoicesIssued: data.invoices_issued_this_month,
        overdueBalance: data.invoices_overdue_this_month
      });
    }

    setLoading(false);
  }

  const applyRange = (range) => {
    setAppliedRange(range);
    const [from, to] = range;
    if (from && to) {
      setSearchParams({
        start_date: format(from, "yyyy-MM-dd"),
        end_date: format(to, "yyyy-MM-dd"),
      });
    }
  };

  const selectDateRangeOption = (option) => {
    setDateRangeOption(option);
    if (option === "custom") return;
    applyRange(getDateRange(option));
    setDateDropdownOpen(false);
  };

  const applyCustomRange = () => {
    if (!startDate || !endDate) return;
    applyRange([startDate, endDate]);
    setDateDropdownOpen(false);
  };

  const dateRangeLabel = dateRangeOption === "custom"
    ? (appliedRange[0] && appliedRange[1] ? `${formatDate(appliedRange[0])} - ${formatDate(appliedRange[1])}` : t("common.custom"))
    : t(`common.${dateRangeOption.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`);

  useEffect(() => {
    getHomeData(appliedRange);
  }, [appliedRange]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('sales.title')} onReload={() => getHomeData(appliedRange)}>
          <Dropdown isOpen={dateDropdownOpen} toggle={() => setDateDropdownOpen(o => !o)} size="sm">
            <DropdownToggle outline color="light" caret>
              <IoCalendarOutline className="me-1" />
              {dateRangeLabel}
            </DropdownToggle>
            <DropdownMenu end>
              {DATE_RANGE_OPTIONS.filter(o => o !== "custom").map(option => (
                <DropdownItem
                  key={option}
                  active={dateRangeOption === option}
                  onClick={() => selectDateRangeOption(option)}
                >
                  {t(`common.${option.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}
                </DropdownItem>
              ))}
              <DropdownItem divider />
              <DropdownItem
                active={dateRangeOption === "custom"}
                toggle={false}
                onClick={() => setDateRangeOption("custom")}
              >
                {t('common.custom')}
              </DropdownItem>
              {dateRangeOption === "custom" && (
                <div className="px-3 py-2 d-flex flex-column gap-2" style={{ minWidth: 220 }}>
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{t('common.from')}</div>
                    <DatePicker
                      selected={startDate}
                      onChange={setStartDate}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={endDate || new Date()}
                      className="form-control form-control-sm"
                      dateFormat={datePickerFormat}
                      placeholderText={t('common.from')}
                    />
                  </div>
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{t('common.to')}</div>
                    <DatePicker
                      selected={endDate}
                      onChange={setEndDate}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      maxDate={new Date()}
                      className="form-control form-control-sm"
                      dateFormat={datePickerFormat}
                      placeholderText={t('common.to')}
                    />
                  </div>
                  <DefaultButton size="sm" disabled={!startDate || !endDate} onClick={applyCustomRange}>
                    {t('common.apply')}
                  </DefaultButton>
                </div>
              )}
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        {loading ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        :
          <div className="subheader-page-content scrollable">
            {/* ── KPI Cards ── */}
            <Row className="g-3 mb-4">
              <Col md="4">
                <StatCard
                  icon={<IoWalletOutline size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={`${kpiData.revenueThisMonth.toLocaleString()} ${currency}`}
                  label={t('crm.revenue')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoReceiptOutline size={18} color="#20c997" />}
                  iconBg="#e6faf5"
                  value={kpiData.invoicesIssued}
                  label={t('sales.invoices')}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoAlertCircleOutline size={18} color="#fa5252" />}
                  iconBg="#fff0f0"
                  value={`${kpiData.overdueBalance.toLocaleString()}  ${currency}`}
                  label={t('common.overdue')}
                />
              </Col>
            </Row>

            {/* ── Revenue Chart + Recent Invoices ── */}
            <Row className="mb-4" style={{ alignItems: "stretch" }}>
              <Col lg="12" className="d-flex flex-column">
                <h6 className="fw-bold">{t('crm.revenue')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-baseline mb-3">
                      <span className="fw-semibold" style={{ fontSize: "0.82rem", color: "#555" }}>
                        {dateRangeLabel}
                      </span>
                    </div>
                    <RevenueBarChart data={monthlyRevenue} />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ── Top Customers + Quote Pipeline ── */}
            <Row className="g-3" style={{ alignItems: "stretch" }}>
              <Col lg="6" className="d-flex flex-column">
                <h6 className="fw-bold">{t('crm.topCustomers')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="p-0">
                    {topCustomersData?.length > 0 && topCustomersData?.map((c, i) => (
                      <div key={i} className="sales-panel-item d-flex align-items-center gap-3 px-3 py-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{ width: 32, height: 32, background: "#f0eeff", fontSize: "0.75rem", fontWeight: 700, color: "#6C63FF" }}
                        >
                          {i + 1}
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-truncate" style={{ fontSize: "0.84rem" }}>
                            {c.company_name || c.firstname + ' ' + c.lastname}
                          </div>
                          <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                            {t('sales.invoiceCount', { count: c.total_invoices })}
                          </div>
                        </div>
                        <div className="fw-bold" style={{ fontSize: "0.84rem" }}>
                          ${c.total_payment_amount}
                        </div>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="6" className="d-flex flex-column">
                <h6 className="fw-bold">{t('sales.quotePipeline')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex align-items-center gap-4 h-100">
                    <QuotePipelineChart data={quotePipeline} />
                    <div className="sales-donut-legend flex-grow-1">
                      {quotePipeline.map((q) => {
                        const total = quotePipeline.reduce((s, x) => s + x.count, 0);
                        return (
                          <div key={q.stage} className="sales-donut-legend-item">
                            <div className="sales-donut-dot" style={{ background: PIPELINE_COLORS[q.stage] }} />
                            <span className="flex-grow-1">{q.stage}</span>
                            <span className="fw-semibold">{q.count}</span>
                            <span className="text-muted ms-2" style={{ fontSize: "0.72rem" }}>
                              {total > 0 ? Math.round((q.count / total) * 100) : 0}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

          </div>
        }
      </div>
    </SidebarLayout>
  );
}
